import { useVirtualizer } from '@tanstack/react-virtual';
import { forwardRef, memo, type RefObject, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { Checkbox } from '@/app/components/ui/checkbox';
import { cn } from '@/app/utils';
import type { ChatMessageItem, ConversationTurnPhase } from '../../types';
import { useAnchorJump } from '../Conversation/hooks/use-anchor-jump';
import { AssistantMessage } from '../Conversation/Messages/AssistantMessage';
import { UserMessage } from '../Conversation/Messages/UserMessage';
import { StoryMessageCard } from '../StoryDetail';

const USER_MESSAGE_ESTIMATE_HEIGHT = 120;
const ASSISTANT_MESSAGE_ESTIMATE_HEIGHT = 380;
const VIRTUAL_LIST_OVERSCAN = 4;
// 跳转后用 RAF 持续比对 scrollTop 与 getOffsetForIndex，连续 N 帧稳定才停；measure 回填会让目标 offset 持续变化。
const CONVERGE_MAX_FRAMES = 40;
const CONVERGE_STABLE_FRAMES = 3;
const CONVERGE_OFFSET_TOLERANCE_PX = 2;
// 容忍图片懒加载这种小幅 totalSize 微动；超过则视为仍在大幅 measure。
const CONVERGE_TOTAL_SIZE_TOLERANCE_PX = 120;

// converge 之后继续观察 totalSize 是否继续扩张，超过阈值则再贴底一次。
const STICKY_BOTTOM_WATCH_MS = 400;
// 阈值过低会被 ~100px 内的小扰动持续触发。
const STICKY_BOTTOM_RETRIGGER_PX = 80;

// 视频 metadata 加载比图片慢，观察窗口比 stickyBottom 长。
const SUB_TARGET_WATCH_MS = 1500;
// 阈值过低会和 measure 微动互相纠缠。
const SUB_TARGET_REALIGN_PX = 4;

export interface MessageListHandle {
  scrollToMessageIndex: (
    index: number,
    options?: {
      align?: 'start' | 'center' | 'end' | 'auto';
      stickyBottomWatch?: boolean;
      /** 跳转完成后让 selector 命中的子元素贴齐视口顶部，用于把 assistant 锚点对齐到 fileList 位置而不是消息开头 */
      subTargetSelector?: string;
    },
  ) => void;
}

interface MessageListProps {
  messages: ChatMessageItem[];
  /** 当前对话轮次阶段：streaming / typing 时携带活跃 assistant id，消费方据此判断单条消息状态 */
  turnPhase: ConversationTurnPhase;
  clawId?: string;
  sessionId?: string;
  onPreviewFile?: (filePath: string) => void;
  onQuoteFile?: (filePath: string) => void;
  onOpenStoryDetail?: (messageId: string) => void;
  onSubmitStory?: (messageId: string) => unknown;
  /** 提交中的分镜消息 id 集合：StoryMessageCard 据此展示 loading */
  submittingStoryMessageIds?: ReadonlySet<string>;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  /** assistant item 高度因伪打字推进而增长时上报；上层据此驱动贴底 + idle 兜底解锁 */
  onAssistantMeasureGrow?: (messageId: string, deltaPx: number) => void;
  /** typer 真正打完时上报；由 AssistantMessage 在 streaming 边沿后等下一次 onEnd 触发 */
  onAssistantTypingEnd?: (messageId: string) => void;
  shareSelection?: {
    enabled: boolean;
    selectedMessageIds: ReadonlySet<string>;
    onToggleMessage: (messageIds: string[]) => void;
  };
}

const MessageListInner = forwardRef<MessageListHandle, MessageListProps>(
  (
    {
      messages,
      turnPhase,
      clawId,
      sessionId,
      onPreviewFile,
      onQuoteFile,
      onOpenStoryDetail,
      onSubmitStory,
      submittingStoryMessageIds,
      scrollContainerRef,
      onAssistantMeasureGrow,
      onAssistantTypingEnd,
      shareSelection,
    },
    ref,
  ) => {
    const virtualizer = useVirtualizer({
      count: messages.length,
      getScrollElement: () => scrollContainerRef.current,
      estimateSize: (index) =>
        messages[index]?.role === 'user' ? USER_MESSAGE_ESTIMATE_HEIGHT : ASSISTANT_MESSAGE_ESTIMATE_HEIGHT,
      overscan: VIRTUAL_LIST_OVERSCAN,
      getItemKey: (index) => messages[index]?.id ?? index,
    });

    // 旧 RAF 读到 token 变化即 stale 退出，避免新旧跳转互抢方向盘。
    const activeJumpTokenRef = useRef(0);
    const { pinSubTargetToTop, watchSubTarget } = useAnchorJump({ scrollRef: scrollContainerRef });

    useImperativeHandle(
      ref,
      () => ({
        scrollToMessageIndex: (index, options) => {
          const align = options?.align ?? 'start';
          const stickyBottomWatch = options?.stickyBottomWatch ?? false;
          const subTargetSelector = options?.subTargetSelector;
          const scrollEl = scrollContainerRef.current;
          if (!scrollEl) {
            return;
          }

          const jumpToken = ++activeJumpTokenRef.current;

          // 用户主动滚动就放弃循环，让用户接管。
          let userInterrupted = false;
          const onUserInteract = () => {
            userInterrupted = true;
          };
          const interactOpts: AddEventListenerOptions = { passive: true, capture: true };
          scrollEl.addEventListener('wheel', onUserInteract, interactOpts);
          scrollEl.addEventListener('touchstart', onUserInteract, interactOpts);
          scrollEl.addEventListener('keydown', onUserInteract, interactOpts);
          const cleanup = () => {
            scrollEl.removeEventListener('wheel', onUserInteract, interactOpts);
            scrollEl.removeEventListener('touchstart', onUserInteract, interactOpts);
            scrollEl.removeEventListener('keydown', onUserInteract, interactOpts);
          };

          // 把 subTargetSelector 命中的子元素贴齐视口顶部，用于把锚点对齐到产物位置而不是消息开头。
          // 返回 true 表示已对齐，调用方据此决定是否启动 subTargetWatch。
          const isStaleOrInterrupted = () => userInterrupted || activeJumpTokenRef.current !== jumpToken;
          const fineTuneToSubTarget = (): boolean => {
            if (!subTargetSelector) return false;
            return pinSubTargetToTop(subTargetSelector, { shouldAbort: isStaleOrInterrupted });
          };

          // fineTune 之后视频/图片懒加载会推动 subTarget 在视口里漂移，watchSubTarget 兜底。
          const startSubTargetWatch = () => {
            if (!subTargetSelector) {
              cleanup();
              return;
            }
            watchSubTarget(subTargetSelector, {
              watchMs: SUB_TARGET_WATCH_MS,
              realignPx: SUB_TARGET_REALIGN_PX,
              shouldAbort: isStaleOrInterrupted,
              onDone: cleanup,
            });
          };

          // bootstrap 专用：converge 完成后继续观察 totalSize 是否扩张，避免 scrollHeight 提前稳定导致没到真底。
          const startStickyBottomWatch = () => {
            const fineTuned = fineTuneToSubTarget();
            if (!stickyBottomWatch) {
              if (fineTuned) {
                startSubTargetWatch();
              } else {
                cleanup();
              }
              return;
            }
            const watchStartedAt = Date.now();
            let lastTotal = virtualizer.getTotalSize();

            const watchTick = () => {
              if (activeJumpTokenRef.current !== jumpToken) {
                cleanup();
                return;
              }
              if (userInterrupted) {
                cleanup();
                return;
              }
              const el = scrollContainerRef.current;
              if (!el) {
                cleanup();
                return;
              }
              const currentTotal = virtualizer.getTotalSize();
              const grown = currentTotal - lastTotal;
              const maxScroll = Math.max(0, el.scrollHeight - el.clientHeight);
              if (align === 'end') {
                // 容器 pb-8 让 scrollHeight 比 totalSize 多 32px，靠 totalSize 扩张判断会漏掉，
                // 直接每帧重写 scrollTop = maxScroll 才能兜住 markdown img / 字体回填的异步 CLS。
                if (Math.abs(el.scrollTop - maxScroll) > 0.5) {
                  el.scrollTop = maxScroll;
                }
                lastTotal = currentTotal;
              } else if (grown > STICKY_BOTTOM_RETRIGGER_PX) {
                lastTotal = currentTotal;
                el.scrollTop = maxScroll;
              } else {
                lastTotal = currentTotal;
              }
              if (Date.now() - watchStartedAt < STICKY_BOTTOM_WATCH_MS) {
                requestAnimationFrame(watchTick);
              } else {
                cleanup();
              }
            };
            requestAnimationFrame(watchTick);
          };

          virtualizer.scrollToIndex(index, { align, behavior: 'auto' });

          // 同时看 offsetStable 和 totalCalm，避免 delta 偶尔 <=2 但 measure 还在大波动的假稳定。
          let frame = 0;
          let stable = 0;
          let lastTotal = virtualizer.getTotalSize();

          const tick = () => {
            frame += 1;

            const el = scrollContainerRef.current;
            if (!el) {
              cleanup();
              return;
            }
            if (activeJumpTokenRef.current !== jumpToken) {
              cleanup();
              return;
            }
            if (userInterrupted) {
              cleanup();
              return;
            }

            const currentTotal = virtualizer.getTotalSize();
            const currentScrollTop = el.scrollTop;
            const maxScroll = Math.max(0, el.scrollHeight - el.clientHeight);

            const offsetResult = virtualizer.getOffsetForIndex?.(index, align);
            const rawTarget = Array.isArray(offsetResult) ? offsetResult[0] : currentScrollTop;
            // 容器 pb-8 让 scrollHeight 比 totalSize 多 32px，对齐到 totalSize 末尾会留 gap，必须用 maxScroll 兜底。
            const clampedTarget = align === 'end' ? maxScroll : Math.min(Math.max(0, rawTarget), maxScroll);
            const delta = clampedTarget - currentScrollTop;
            const offsetStable = Math.abs(delta) <= CONVERGE_OFFSET_TOLERANCE_PX;
            const totalDelta = Math.abs(currentTotal - lastTotal);
            const totalCalm = totalDelta <= CONVERGE_TOTAL_SIZE_TOLERANCE_PX;

            if (offsetStable && totalCalm) {
              stable += 1;
              if (stable >= CONVERGE_STABLE_FRAMES) {
                startStickyBottomWatch();
                return;
              }
            } else {
              stable = 0;
              if (!offsetStable) {
                // 直接写 scrollTop 比再调一次 scrollToIndex 更可控；scrollToIndex 内部会发二次对齐和本循环互相覆盖。
                el.scrollTop = clampedTarget;
              }
            }

            lastTotal = currentTotal;

            if (frame < CONVERGE_MAX_FRAMES) {
              requestAnimationFrame(tick);
            } else {
              startStickyBottomWatch();
            }
          };
          requestAnimationFrame(tick);
        },
      }),
      [virtualizer, messages, scrollContainerRef, pinSubTargetToTop, watchSubTarget],
    );

    const renderMessage = (message: ChatMessageItem) => {
      if (message.story) {
        return (
          <StoryMessageCard
            key={message.id}
            story={message.story}
            onOpenDetail={() => onOpenStoryDetail?.(message.id)}
            onSubmitAll={() => onSubmitStory?.(message.id)}
            submitting={submittingStoryMessageIds?.has(message.id) ?? false}
          />
        );
      }

      return message.role === 'user' ? (
        <UserMessage
          key={message.id}
          message={message}
          clawId={clawId}
          onPreviewFile={onPreviewFile}
          onQuoteFile={onQuoteFile}
        />
      ) : (
        <AssistantMessage
          key={message.id}
          message={message}
          isActiveConversationTool={turnPhase.status === 'streaming' && message.id === turnPhase.assistantMessageId}
          isTypingPlayback={turnPhase.status === 'typing' && message.id === turnPhase.assistantMessageId}
          clawId={clawId}
          sessionId={sessionId}
          onPreviewFile={onPreviewFile}
          onQuoteFile={onQuoteFile}
          onTypingEnd={onAssistantTypingEnd}
        />
      );
    };

    const virtualItems = virtualizer.getVirtualItems();
    const totalSize = virtualizer.getTotalSize();

    // tanstack-virtual 内部 RO 不经过 ref callback，markdown 图片/打字机收尾/字体回填这些异步 CLS 拿不到，
    // 必须靠独立 RO 触发上层 sticky-bottom / idle 解锁。
    const measuredSizeRef = useRef<Map<string | number, number>>(new Map());
    const itemObserverRef = useRef<Map<HTMLElement, ResizeObserver>>(new Map());
    const messagesRef = useRef(messages);
    messagesRef.current = messages;
    // 用 ref 中转最新回调，避免 measureItem 依赖回调引用导致 RO 频繁重挂。
    const onAssistantMeasureGrowRef = useRef(onAssistantMeasureGrow);
    onAssistantMeasureGrowRef.current = onAssistantMeasureGrow;

    useEffect(() => {
      const observers = itemObserverRef.current;
      return () => {
        observers.forEach((ro) => {
          ro.disconnect();
        });
        observers.clear();
      };
    }, []);

    const measureItem = useCallback(
      (el: HTMLDivElement | null) => {
        const observers = itemObserverRef.current;
        if (!el) {
          return;
        }
        const rawIndex = el.getAttribute('data-index');
        const index = rawIndex === null ? -1 : Number(rawIndex);
        const currentMessages = messagesRef.current;
        const key = index >= 0 ? (currentMessages[index]?.id ?? index) : null;
        const size = el.getBoundingClientRect().height;
        const prev = key !== null ? measuredSizeRef.current.get(key) : undefined;

        // assistant item 重 mount 第一帧只有 ≈100px 外壳，下一帧才回填真实高度。
        // 直接上报会让 totalSize 瞬间塌陷几千 px，跳转 RAF 反复拉锯产生剧烈抖动。
        // 缩到 ≤ 50% 且 cached > 200 时跳过，等 RO 拿到真实高度再补；期间 tanstack-virtual 内部 RO 兜底。
        const shouldSkipReport = prev !== undefined && prev > 200 && size < prev * 0.5;
        if (!shouldSkipReport) {
          virtualizer.measureElement(el);
        }

        if (rawIndex === null) return;
        if (prev === undefined) {
          if (key !== null) measuredSizeRef.current.set(key, size);
        } else if (!shouldSkipReport && Math.abs(prev - size) > 0.5) {
          if (key !== null) measuredSizeRef.current.set(key, size);
        }

        // 持久 RO，只在首次为同一 el 注册。
        if (typeof ResizeObserver === 'undefined') return;
        if (observers.has(el)) return;
        let lastSize = size;
        const ro = new ResizeObserver((entries) => {
          for (const entry of entries) {
            const target = entry.target as HTMLElement;
            const rawIdx = target.getAttribute('data-index');
            if (rawIdx === null) continue;
            const idx = Number(rawIdx);
            const msgs = messagesRef.current;
            const msgKey = msgs[idx]?.id ?? idx;
            const h = entry.contentRect.height;
            if (Math.abs(h - lastSize) <= 0.5) continue;
            const delta = h - lastSize;
            const grewFromShell = lastSize < 200 && h > 200;
            lastSize = h;
            measuredSizeRef.current.set(msgKey, h);
            // 上报 assistant 增长，给上层做 idle/贴底派生信号源。
            if (delta > 0 && msgs[idx]?.role === 'assistant' && typeof msgKey === 'string') {
              onAssistantMeasureGrowRef.current?.(msgKey, delta);
            }
            // 第一帧被判定为 shell 时跳过上报，等 RO 看到真实高度后补一次 measureElement，让 totalSize 一次到位避免回弹。
            if (grewFromShell) {
              virtualizer.measureElement(target);
            }
          }
        });
        ro.observe(el);
        observers.set(el, ro);

        // ResizeObserver 没有 onDisconnect，借 MutationObserver 盯父节点 childList，等元素被卸载时回收 RO。
        const parent = el.parentElement;
        if (parent) {
          const mo = new MutationObserver(() => {
            if (!el.isConnected) {
              ro.disconnect();
              observers.delete(el);
              mo.disconnect();
            }
          });
          mo.observe(parent, { childList: true });
        }
      },
      [virtualizer],
    );

    return (
      <div className="relative w-full" style={{ height: totalSize }}>
        {virtualItems.map((virtualItem) => {
          const message = messages[virtualItem.index];
          if (!message) return null;

          const shareMessageIds = message.shareMessageIds ?? (message.serverMessageId ? [message.serverMessageId] : []);
          const canSelectMessage = shareMessageIds.length > 0;
          const isSelected =
            canSelectMessage && shareMessageIds.every((messageId) => shareSelection?.selectedMessageIds.has(messageId));
          const canToggleShareSelection = !!shareSelection?.enabled && canSelectMessage;

          return (
            <div
              key={message.id}
              ref={measureItem}
              data-index={virtualItem.index}
              data-msg-id={message.serverMessageId}
              data-turn-id={message.turnId}
              className={cn(
                'absolute top-0 left-0 w-full',
                shareSelection?.enabled && 'rounded-12 transition-colors',
                canToggleShareSelection && 'cursor-pointer',
                shareSelection?.enabled && isSelected && 'bg-system-white08 light:bg-[rgba(87,101,242,0.08)]',
              )}
              style={{ transform: `translateY(${virtualItem.start}px)` }}
              onClickCapture={(event) => {
                if (shareSelection?.enabled) {
                  event.preventDefault();
                  event.stopPropagation();
                  if (canToggleShareSelection) {
                    shareSelection.onToggleMessage(shareMessageIds);
                  }
                }
              }}
            >
              {shareSelection?.enabled ? (
                <div className="-left-6 absolute top-6 z-10" onClick={(event) => event.stopPropagation()}>
                  <Checkbox
                    checked={isSelected}
                    disabled={!canSelectMessage}
                    className="light:text-system-text04 text-base text-system-white64 disabled:cursor-not-allowed disabled:opacity-30 data-[state=checked]:text-[#5765F2]"
                    onCheckedChange={() => {
                      if (canSelectMessage) {
                        shareSelection.onToggleMessage(shareMessageIds);
                      }
                    }}
                  />
                </div>
              ) : null}
              <div className="min-w-0">{renderMessage(message)}</div>
            </div>
          );
        })}
      </div>
    );
  },
);

MessageListInner.displayName = 'MessageList';

export const MessageList = memo(MessageListInner);
